import React, { useCallback, useEffect, useMemo, useState } from 'react';
import T from 'i18n-react';

import FilterableVehicleList from 'Components/pages/VehiclesList/FilterableVehicleList';
import { LIST_TYPE_BRAZE_DICT } from 'Components/VehicleList/VehicleList.consts';
import { VehicleListV2 } from 'Components/VehicleList/VehicleListV2/VehicleListV2';
import { useBidding } from 'Context/BiddingContext';
import { triggerCloseButtonClickedEvent } from 'Services/analytics/events';
import { useBraze } from 'Services/braze';
import {
	useAppliedFiltersCount,
	useIsFiltersMenuOpen,
	useSetIsFiltersMenuOpen,
} from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useListType } from 'Stores/ListStore/ListStore';
import { useSetIsSavedSearchPillOpen } from 'Stores/SavedFiltersStore/SavedFiltersStore';
import type { Filter } from 'Types/filter';
import type { Pagination } from 'Types/pagination';
import type { VehicleListItem } from 'Types/vehicle';
import { LIST_TYPES } from 'Utilities/consts';
import { prettyPostcode } from 'Utilities/formatters';
import useCallbackOnTimeout from 'Utilities/hooks/useCallbackOnTimeout';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useUser from 'Utilities/hooks/useUser';
import useShouldFetchVehicles from 'Utilities/hooks/vehicleList/useShouldFetchVehicles';
import { useClearVehicleListCache } from 'Utilities/hooks/vehicleList/useVehicleListCache/useClearVehicleListCache';

import texts from '../texts.json';

import { useSeenFilterAutoLoad } from './useSeenFilterAutoLoad';
import { vehicleListRefreshObservable } from './VehicleList.helpers';

T.setTexts(texts);

export interface VehicleListProps {
	filters: Filter;
	isError: boolean;
	isLoading: boolean;
	onLoadMore: () => void;
	onRefresh: () => void;
	pagination: Pagination;
	vehicleCount: number;
	vehicles: VehicleListItem[];
}

const VehicleListLegacy: React.FC<VehicleListProps> = ({
	filters,
	isError,
	isLoading,
	onLoadMore,
	onRefresh,
	pagination,
	vehicleCount,
	vehicles,
}) => {
	const [shouldDisplayRefreshPrompt, setShouldDisplayRefreshPrompt] = useState(false);
	const user = useUser();
	const { clearVehicleIds } = useBidding();
	const appliedFiltersCount = useAppliedFiltersCount();
	const isFiltersMenuOpen = useIsFiltersMenuOpen();
	const setIsFiltersMenuOpen = useSetIsFiltersMenuOpen();
	const setIsSavedSearchPillOpen = useSetIsSavedSearchPillOpen();
	const { isLiveSaleEnabled, isSaleActive, todaysSaleEndsAt } = useSaleTimes();
	const listType = useListType();
	const { clearAllVehicleListCache } = useClearVehicleListCache();
	const isShortlist = listType === LIST_TYPES.shortlist;

	useBraze({
		eventFn: LIST_TYPE_BRAZE_DICT[listType],
	});

	const onVehicleListRefresh = useCallback(
		({ shouldDisplayRefresh = false } = {}) => {
			setShouldDisplayRefreshPrompt(shouldDisplayRefresh);

			if (!shouldDisplayRefresh) {
				onRefresh();
			}
		},
		[setShouldDisplayRefreshPrompt, onRefresh],
	);

	useCallbackOnTimeout({
		callback: () => onVehicleListRefresh({ shouldDisplayRefresh: true }),
		condition: isShortlist,
		time: todaysSaleEndsAt,
	});

	useEffect(() => {
		/*
		 * Observable was require as this component was previously a class component
		 * and were unable to use zustand in it.
		 * Review this useEffect when moving to a hook.
		 */
		vehicleListRefreshObservable.subscribe(onVehicleListRefresh);
		return () => {
			clearVehicleIds();
			vehicleListRefreshObservable.unsubscribe(onVehicleListRefresh);
		};
	}, [clearVehicleIds, onVehicleListRefresh]);

	useEffect(() => {
		if (shouldDisplayRefreshPrompt) {
			setShouldDisplayRefreshPrompt(isShortlist);
		}
	}, [isShortlist, shouldDisplayRefreshPrompt]);

	useEffect(
		() => () => {
			setIsSavedSearchPillOpen(false);
			setIsFiltersMenuOpen(false);
		},
		[setIsFiltersMenuOpen, setIsSavedSearchPillOpen],
	);

	const toggleFilters = (isSidebarClose = false) => {
		if (isSidebarClose) {
			triggerCloseButtonClickedEvent();
		}
		setIsFiltersMenuOpen(!isFiltersMenuOpen);
	};

	const onSidebarCloseClick = () => toggleFilters(true);
	const refreshVehicleList = () => {
		setShouldDisplayRefreshPrompt(false);
		clearAllVehicleListCache();
		window.scrollTo(0, 0);
		onRefresh();
	};

	const { filteredVehicles, vehiclesCount } = useSeenFilterAutoLoad({
		pagination,
		vehicleCount,
		vehicles,
	});

	const dealerLatLng: [number, number] = useMemo(() => [user?.latitude, user?.longitude], [user]);
	const dealerPostcode: string = useMemo(() => prettyPostcode(user?.postcode), [user]);

	const shouldFetchVehicleList = useShouldFetchVehicles();
	const noVehiclesFound = (vehicles.length === 0 || filteredVehicles.length === 0) && !isLoading;
	const shouldShowLiveSaleDisabledMessage = !isLiveSaleEnabled && isSaleActive && listType === LIST_TYPES.auction;
	const shouldShowNoSaleMessage =
		!shouldFetchVehicleList || (noVehiclesFound && appliedFiltersCount === 0) || shouldShowLiveSaleDisabledMessage;

	const noSaleProps = useMemo(() => {
		return shouldShowLiveSaleDisabledMessage
			? {
					messageProp: `${T.translate('vehicleList.saleDisabledMessage')}`,
					titleProp: `${T.translate('vehicleList.saleDisabledTitle')}`,
				}
			: {};
	}, [shouldShowLiveSaleDisabledMessage]);

	return (
		<FilterableVehicleList
			dealerLatLng={dealerLatLng}
			dealerPostcode={dealerPostcode}
			filters={filters}
			isError={isError}
			isLoading={isLoading}
			isOpenFilters={isFiltersMenuOpen}
			loadMore={onLoadMore}
			noSaleProps={noSaleProps}
			noVehiclesFound={noVehiclesFound}
			onSidebarCloseClick={onSidebarCloseClick}
			onVehicleListRefreshButtonClick={refreshVehicleList}
			pagination={pagination}
			shouldDisplayRefreshPrompt={shouldDisplayRefreshPrompt}
			shouldShowNoSaleMessage={shouldShowNoSaleMessage}
			togglePanelFilters={toggleFilters}
			vehicles={filteredVehicles}
			vehiclesCount={vehiclesCount}
		/>
	);
};

const VehicleList: React.FC<VehicleListProps> = ({
	filters,
	isError,
	isLoading,
	onLoadMore,
	onRefresh,
	pagination,
	vehicleCount,
	vehicles,
}) => {
	const isProInfoHierarchyEnabled = useFeatureToggle(FEATURES.proInfoHierarchy);

	if (isProInfoHierarchyEnabled) {
		return <VehicleListV2 isLoading={isLoading} onLoadMore={onLoadMore} pagination={pagination} vehicles={vehicles} />;
	}

	return (
		<VehicleListLegacy
			filters={filters}
			isError={isError}
			isLoading={isLoading}
			onLoadMore={onLoadMore}
			onRefresh={onRefresh}
			pagination={pagination}
			vehicleCount={vehicleCount}
			vehicles={vehicles}
		/>
	);
};

export default VehicleList;
